// Azure Static Web Apps built-in Entra ID authentication service
// Uses SWA platform auth (/.auth/login/aad, /.auth/me) instead of MSAL
import type { UserRole, AuthUser } from "@/types";

const DEV_MODE = import.meta.env.DEV && !import.meta.env.VITE_ENTRA_CLIENT_ID;

// --- SWA client principal types ---

interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims: { typ: string; val: string }[];
}

interface SwaAuthResponse {
  clientPrincipal: SwaClientPrincipal | null;
}

// --- Dev mode auth (no Entra required locally) ---

const DEV_AUTH_KEY = "ownertoagent_dev_auth";

interface DevAuth {
  role: UserRole;
  userId: string;
  name: string;
  email: string;
}

function getDevAuth(): DevAuth | null {
  const stored = sessionStorage.getItem(DEV_AUTH_KEY);
  if (!stored) return null;
  try { return JSON.parse(stored); } catch { return null; }
}

function setDevAuth(auth: DevAuth): void {
  sessionStorage.setItem(DEV_AUTH_KEY, JSON.stringify(auth));
}

function clearDevAuth(): void {
  sessionStorage.removeItem(DEV_AUTH_KEY);
}

export function getDevToken(): string | null {
  if (!DEV_MODE) return null;
  const auth = getDevAuth();
  if (!auth) return null;
  return `dev_${auth.role}_${auth.userId}`;
}

export function isDevMode(): boolean {
  return DEV_MODE;
}

// --- SWA built-in auth ---

let cachedPrincipal: SwaClientPrincipal | null = null;

export async function fetchClientPrincipal(): Promise<SwaClientPrincipal | null> {
  if (DEV_MODE) return null;
  try {
    const res = await fetch("/.auth/me");
    if (!res.ok) return null;
    const data: SwaAuthResponse = await res.json();
    cachedPrincipal = data.clientPrincipal;
    return cachedPrincipal;
  } catch {
    return null;
  }
}

export async function login(role?: UserRole, redirectTo?: string): Promise<void> {
  if (DEV_MODE) {
    const selectedRole = role || "owner";
    let userId: string;
    let name: string;

    if (selectedRole === "agent") {
      // Pick agent via sessionStorage override or default to Joe Cayman
      const agents: Record<string, string> = {
        "dev-agent-joe": "Joe Cayman",
        "dev-agent-annatjie": "Annatjie Otto",
        "da1568ac-78af-4eeb-af0d-bf9c8061aad4": "Nadia Mokoena",
      };
      const override = sessionStorage.getItem("ownertoagent_dev_agent");
      userId = override && agents[override] ? override : "dev-agent-joe";
      name = agents[userId] || "Joe Cayman";
    } else if (selectedRole === "owner") {
      userId = "dev-owner-1";
      name = "Dev Owner";
    } else {
      userId = "dev-admin-1";
      name = "Dev Admin";
    }

    setDevAuth({ role: selectedRole, userId, name, email: `${selectedRole}@localhost` });
    return;
  }
  // Redirect to SWA built-in Entra ID login
  const returnUrl = encodeURIComponent(redirectTo || window.location.pathname);
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
}

export async function logout(): Promise<void> {
  if (DEV_MODE) {
    clearDevAuth();
    return;
  }
  window.location.href = "/.auth/logout?post_logout_redirect_uri=/";
}

export async function getAccessToken(): Promise<string | null> {
  // SWA built-in auth uses cookies — no Bearer token needed for /api/* calls
  // The SWA reverse proxy automatically attaches x-ms-client-principal
  if (DEV_MODE) return getDevToken();
  return null;
}

export function getActiveUser(): AuthUser | null {
  if (DEV_MODE) {
    const auth = getDevAuth();
    if (!auth) return null;
    return {
      id: auth.userId,
      email: auth.email,
      role: auth.role,
      name: auth.name,
    };
  }
  return mapPrincipalToUser(cachedPrincipal);
}

export function mapPrincipalToUser(principal: SwaClientPrincipal | null): AuthUser | null {
  if (!principal) return null;

  const roles = principal.userRoles || [];
  const role: UserRole = roles.includes("admin") ? "admin"
    : roles.includes("agent") ? "agent"
    : "owner";

  const nameClaim = principal.claims?.find(c => c.typ === "name")?.val;
  const emailClaim = principal.claims?.find(c => c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress")?.val
    || principal.userDetails;

  return {
    id: principal.userId,
    email: emailClaim,
    role,
    name: nameClaim || undefined,
  };
}
